import {
  validateFilterOptions,
  type TableFilterOptionsResponse
} from '../../../state_manager/rpc_models/get_table_filter_options_response.ts'
import { validateDealRecord, type DealRecord, DealFields } from '../../../state_manager/data_models/deal_record.ts'
import {
  validateDealMobileListRecord,
  type DealMobileListRecord
} from '../../../state_manager/data_models/deal_mobile_list_record.ts'
import { FilterValue, SortConfig } from '../../../state_manager/store_models/deals.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import { validateDealCommitmentEntry } from '../../../state_manager/data_models/deal_commitment_entry.ts'
import {
  validateDealPipelineBudget,
  type DealPipelineBudget
} from '../../../state_manager/data_models/deal_pipeline_budget.ts'
import { formatKeyToLabel, readBackendErrorMessage } from '../../../core/utils/utilities.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../core/components/FormTable/constants.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface DealsPaginatedResponse {
  data: DealRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface DealsMobileListResponse {
  data: DealMobileListRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

/**
 * Lean mobile deals list — hits `/deals/mobile-list` (the view__deals_mobile_list RPC),
 * returning only id + name + company + stage tag + Val/raise/commitment, server-scoped to
 * the default Deal Origination pipeline (same deal set as the grid). ~40x faster and a
 * fraction of the payload of `fetchDealsFiltered` (which pulls embedded tasks/contacts).
 * `filters` carries the optional Stage multiselect (pipeline_deals__stage).
 */
export const fetchDealsMobileList = async (
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number = 25,
  accessToken: string | null = null
): Promise<DealsMobileListResponse> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const requestBody = { data: { filters, page, page_size: pageSize } }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.MOBILE_LIST), {
      method: API_ROUTES.DEALS.MOBILE_LIST.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch mobile deals list: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateDealMobileListRecord(parsed.data || [], { isArray: true }) as DealMobileListRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Unexpected error fetching mobile deals list:', error)
    }
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching deals'
    }
  }
}

/**
 * Fetches deals with pagination support for infinite scroll
 * Updated to use backend API instead of direct Supabase calls
 * @param page - Page number (1-based)
 * @param pageSize - Number of records per page (default: 200)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with deals data, pagination info, and validation
 */
export const fetchDealsPaginated = async (
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  reportId?: string | null
): Promise<DealsPaginatedResponse> => {
  try {
    // Build query parameters - skip pagination if pageSize is null
    const queryParams = new URLSearchParams()

    if (pageSize !== null) {
      queryParams.append('page', page.toString())
      queryParams.append('pageSize', pageSize.toString())
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(
      buildFullUrl(
        BACKEND_BASE_URL,
        API_ROUTES.DEALS.LIST,
        {},
        {
          ...(pageSize !== null && { page: page.toString(), pageSize: pageSize.toString() }),
          ...(reportId ? { report_id: reportId } : {})
        }
      ),
      {
        method: API_ROUTES.DEALS.LIST.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch deals: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateDealRecord(parsed.data || [], { isArray: true }) as DealRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    console.error('Unexpected error fetching paginated deals:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching deals'
    }
  }
}

/**
 * Fetches deals with filtering and sorting using POST endpoint
 * @param sorting - Sort configuration
 * @param filters - Array of filter values
 * @param accessToken - User's access token from Redux store
 * @returns Promise with deals data, pagination info, and validation
 */
export const fetchDealsFiltered = async (
  sorting: SortConfig[] = [],
  filters: FilterValue[] = [],
  page: number = 1,
  pageSize: number | null = null,
  accessToken: string | null = null,
  reportId?: string | null
): Promise<DealsPaginatedResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const effectivePageSize = pageSize ?? 200

    // Build request body
    const requestBody: {
      data: {
        sorting?: Array<{ key: string; direction: string }>
        filters?: Array<{ key: string; value: string[] | string | { min?: number | string; max?: number | string } }>
        page?: number
        page_size?: number
        report_id?: string
      }
    } = {
      data: {
        page,
        page_size: effectivePageSize
      }
    }

    // Add sorting if provided
    requestBody.data.sorting = sorting.map((sort) => ({
      key: sort.key,
      direction: sort.direction
    }))

    // Add filters if provided
    if (filters.length > 0) {
      requestBody.data.filters = filters.map((filter) => ({
        key: filter.key,
        value: filter.value
      }))
    }

    if (reportId) {
      requestBody.data.report_id = reportId
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.FILTER), {
      method: API_ROUTES.DEALS.FILTER.method,
      headers,
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch filtered deals: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[]; has_more?: boolean; count?: number }

    const validatedData = validateDealRecord(parsed.data || [], { isArray: true }) as DealRecord[]

    return {
      data: validatedData,
      hasMore: parsed.has_more || false,
      totalCount: parsed.count || 0
    }
  } catch (error) {
    console.error('Unexpected error fetching filtered deals:', error)
    return {
      data: [],
      hasMore: false,
      totalCount: 0,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching deals'
    }
  }
}

export const fetchDealsFilterOptions = async (
  accessToken: string | null = null,
  requestedKeys?: string[]
): Promise<TableFilterOptionsResponse> => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`

  const body: Record<string, unknown> = { data: {} }
  if (requestedKeys && requestedKeys.length > 0) {
    body.data = { requested_keys: requestedKeys }
  }

  const res = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.FILTER_OPTIONS), {
    method: API_ROUTES.DEALS.FILTER_OPTIONS.method,
    headers,
    body: JSON.stringify(body)
  })

  if (!res.ok) {
    if (res.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(`Failed to fetch filter options: ${res.status} ${res.statusText}`)
  }

  const json: unknown = await res.json()
  const parsed = json as { data?: Record<string, unknown> }
  return { data: validateFilterOptions(parsed.data || {}) }
}

export interface CreateDealResponse {
  data: DealRecord | null
  created_ids?: Record<string, string>
  error?: string
}

/**
 * Creates a new deal with validation
 * @param dealData - The deal data to create (should be DealRecord format)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated deal record or error
 */
export const createDeal = async (
  dealData: Record<string, any>,
  accessToken: string | null = null
): Promise<CreateDealResponse> => {
  try {
    // Filter to only include DealFields constants (safety check)
    const validDealFields = new Set<string>(Object.values(DealFields))
    const cleanedData: Record<string, any> = {}

    Object.keys(dealData).forEach((key) => {
      if (validDealFields.has(key)) {
        cleanedData[key] = dealData[key]
      }
    })

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.CREATE), {
      method: API_ROUTES.DEALS.CREATE.method,
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 400) {
        const errorResult = await response.json()

        // Check if this is a duplicate error
        if (errorResult.message === 'Duplicate records found' && errorResult.data) {
          // Show toast for each duplicate field
          Object.entries(errorResult.data).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              const formattedLabel = formatKeyToLabel(key, true, true)
              const duplicateValues = value.join(', ')
              showErrorToast(`Duplicate ${formattedLabel} found: ${duplicateValues}`, TOAST_CONFIG)
            }
          })
        }

        // Still throw error to prevent form submission
        throw new Error(await readBackendErrorMessage(response, 'Failed to create deal', errorResult))
      }
      // Any other failure (5xx, timeout, RLS): the backend's own sentence,
      // never a bare status line the user cannot act on.
      throw new Error(await readBackendErrorMessage(response, 'Failed to create deal'))
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown; created_ids?: Record<string, string> }

    if (parsed.data) {
      const validatedData = validateDealRecord(parsed.data)
      return {
        data: validatedData,
        created_ids: parsed.created_ids
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    console.error('Error creating deal:', error)
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while creating deal'
    }
  }
}

export interface FetchDealByIdResponse {
  data: DealRecord | null
  error?: string
}

/**
 * Fetches a single deal by ID
 * @param dealId - The ID of the deal to fetch
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated deal record or error
 */
export const fetchDealById = async (
  dealId: string | number,
  accessToken: string | null = null
): Promise<FetchDealByIdResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.GET, { id: dealId }), {
      method: API_ROUTES.DEALS.GET.method,
      headers
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Deal not found')
      }
      throw new Error(`Failed to fetch deal: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateDealRecord(parsed.data)
      return {
        data: validatedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    console.error('Error fetching deal by ID:', error)
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while fetching deal'
    }
  }
}

export const deleteDeal = async (dealId: string | number, accessToken: string | null = null): Promise<void> => {
  const headers: Record<string, string> = {}
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.DELETE, { id: dealId }), {
    method: API_ROUTES.DEALS.DELETE.method,
    headers
  })
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed - please log in again')
    }
    const errorBody = await response.json().catch(() => ({}))
    throw new Error((errorBody as { message?: string }).message || 'Failed to delete deal')
  }
}

export interface UpdateDealResponse {
  data: DealRecord | null
  error?: string
}

/**
 * Updates an existing deal
 * Note: For form field updates, InputManager auto-patch is preferred.
 * This function is kept for programmatic updates (e.g., drag and drop operations).
 * @param dealId - The ID of the deal to update
 * @param dealData - The deal data to update (partial updates supported)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated deal record or error
 */
export const updateDeal = async (
  dealId: string | number,
  dealData: Record<string, any>,
  accessToken: string | null = null
): Promise<UpdateDealResponse> => {
  try {
    // Clean up the data before sending
    const cleanedData: Record<string, any> = { ...dealData }

    // Only convert numeric fields if they were actually provided in the original data
    const numericFields = [
      'allocation_available',
      'raising_amount',
      'pre_money_valuation',
      'post_money_valuation',
      'lane_priority'
    ]
    numericFields.forEach((field) => {
      if (dealData.hasOwnProperty(field)) {
        cleanedData[field] = dealData[field] ? parseFloat(String(dealData[field])) : null
      }
    })

    // Process commitments array - ensure amounts are numbers
    if (dealData.commitments) {
      cleanedData.commitments = dealData.commitments.map((commitment: any) => ({
        ...commitment,
        amount: commitment.amount ? parseFloat(String(commitment.amount)) : null
      }))
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.UPDATE, { id: dealId }), {
      method: API_ROUTES.DEALS.UPDATE.method,
      headers,
      body: JSON.stringify(cleanedData)
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 404) {
        throw new Error('Deal not found')
      }
      if (response.status === 400) {
        const errorResult = await response.json()
        throw new Error((errorResult as { message?: string }).message || 'Invalid data provided')
      }
      throw new Error(`Failed to update deal: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    if (parsed.data) {
      const validatedData = validateDealRecord(parsed.data)
      return {
        data: validatedData
      }
    }

    return {
      data: null,
      error: 'No data returned from server'
    }
  } catch (error) {
    console.error('Error updating deal:', error)
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while updating deal'
    }
  }
}

export interface UpdateDealCommitmentsResponse {
  data: any | null
  error?: string
}

/**
 * Updates deal commitments
 * @param dealId - The ID of the deal to update commitments for
 * @param commitments - Array of commitment objects in DealCommitmentEntrySchema format (strict requirement)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with response data or error
 */
export const updateDealCommitments = async (
  dealId: string | number,
  commitments: Array<any>,
  accessToken: string | null = null
): Promise<UpdateDealCommitmentsResponse> => {
  try {
    // Validate all commitments are in schema format
    const validatedCommitments = commitments.map((commitment, index) => {
      try {
        validateDealCommitmentEntry(commitment)
        return commitment
      } catch (error) {
        console.error(`Validation failed for commitment at index ${index}:`, error)
        const errorMessage = error instanceof Error ? error.message : 'Validation failed'
        throw new Error(`Commitment validation failed at index ${index}: ${errorMessage}`)
      }
    })

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    // Send schema-formatted data to backend
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.COMMITMENTS, { id: dealId }), {
      method: API_ROUTES.DEALS.COMMITMENTS.method,
      headers,
      body: JSON.stringify({ commitments: validatedCommitments })
    })

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      if (response.status === 400) {
        const errorResult = await response.json()
        throw new Error((errorResult as { message?: string }).message || 'Invalid commitment data provided')
      }
      throw new Error(`Failed to update commitments: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown }

    return {
      data: parsed.data || parsed
    }
  } catch (error) {
    console.error('Error updating deal commitments:', error)
    return {
      data: null,
      error: error instanceof Error ? error.message : 'An unexpected error occurred while updating commitments'
    }
  }
}

export interface SearchDealsForPopupResponse {
  data: DealRecord[]
  error?: string
}

/**
 * Searches deals for toolbar search popup (independent of table filters)
 * @param searchTerm - The search term to look for
 * @param limit - Maximum number of results to return (default: 10)
 * @param accessToken - User's access token from Redux store
 * @returns Promise with validated deal records or error
 */
export const searchDealsForPopup = async (
  searchTerm: string,
  limit: number = 10,
  accessToken: string | null = null
): Promise<SearchDealsForPopupResponse> => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return { data: [] }
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const queryParams = new URLSearchParams({
      search: searchTerm.trim(),
      limit: limit.toString()
    })

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.SEARCH, {}, Object.fromEntries(queryParams)),
      {
        method: API_ROUTES.DEALS.SEARCH.method,
        headers
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to search deals: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: unknown[] }

    if (parsed.data) {
      // Limit results and validate
      const limitedData = parsed.data.slice(0, limit)
      const validatedData = validateDealRecord(limitedData, { isArray: true }) as DealRecord[]
      return {
        data: validatedData
      }
    }

    return {
      data: []
    }
  } catch (error) {
    console.error('Error searching deals for popup:', error)
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred while searching deals'
    }
  }
}

/**
 * Deal Pipeline → Budget tab: the capital and cash position of the followed
 * funds and their open deal commitments, one call for every fund. An empty
 * or null fund list asks for the cfg default funds; the response names the
 * funds it resolved to.
 */
export const fetchDealPipelineBudget = async (
  fundIds: string[] | null,
  accessToken: string | null = null
): Promise<{ data: DealPipelineBudget | null; error?: string }> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DEALS.PIPELINE_BUDGET), {
      method: API_ROUTES.DEALS.PIPELINE_BUDGET.method,
      headers,
      body: JSON.stringify({ data: { fund_ids: fundIds && fundIds.length > 0 ? fundIds : null } })
    })
    if (!response.ok) {
      throw new Error(await readBackendErrorMessage(response, `Failed to fetch the pipeline budget: ${response.status}`))
    }
    const json = (await response.json()) as { data?: unknown }
    return { data: validateDealPipelineBudget(json.data ?? {}) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching the pipeline budget:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
